import React from "react";
import UserAvatar from "@/components/controls/UserAvatar";
import Text from "@/components/ui/text";
import { find, map, uniqueId } from "lodash";
import { ConversionRatesChartDataType } from "@/components/layouts/ConversionRates/ConversionRates";
import { useConversionBreadcrumbs } from "@/components/layouts/ConversionRates/ConversionBreadcrumbsContext";
import { Progress } from "@/components/ui/progress";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { reducePrecision } from "@/utils/numbers";
import DrilldownSkeleton from "@/components/ui/drilldown-skeleton";
import { cn } from "@/utils/utils";

interface Props {
  selectedElement?: ConversionRatesChartDataType;
  selectedStageId: string;
  selectedTab: string;
}

const ConversionDrilldown: React.FC<Props> = ({
  selectedElement,
  selectedStageId,
  selectedTab
}) => {
  const { dynamicTableData, handleRowClick, directReportsData, isLoading } =
    useConversionBreadcrumbs();

  const indicatorColor = getColorForMilestoneByType(
    selectedElement?.milestone,
    "border"
  );
  const barColor = getColorForMilestoneByType(
    selectedElement?.milestone,
    "background"
  );

  const getConversionData = (userId: string): number => {
    const directReport = find(
      directReportsData?.directReports,
      (report) => report.id === userId
    );

    if (!directReport || !selectedStageId) return 0;

    // Determine which collection to search based on the selected tab
    const data =
      selectedTab === "stages" ? directReport.stages : directReport.milestones;

    // Find the matching stage/milestone
    const match = find(
      data,
      (phase) => phase.id.toString() === selectedStageId
    );

    return reducePrecision(match?.conversions.allToCurrent || 0);
  };

  return (
    <div
      className={cn(
        "bg-surface-elevation0 flex flex-col py-6 gap-10 min-h-full w-full",
        isLoading ? "gap-8" : "gap-2"
      )}
    >
      {map(dynamicTableData, (directReport) => {
        const conversionPercentage = getConversionData(directReport?.id);

        return isLoading ? (
          <DrilldownSkeleton />
        ) : (
          <div
            key={uniqueId()}
            className="flex gap-2 px-4 py-1 max-h-12 items-center"
            onClick={() => handleRowClick(directReport)}
          >
            <div className="w-52 h-fit">
              <div className="flex gap-2 w-fit px-2 py-1 rounded-lg cursor-pointer hover:bg-surface-hover">
                <UserAvatar userData={directReport} size="M" />
                <Text as="div" variant="body">
                  {directReport?.displayName}
                </Text>
              </div>
            </div>
            <div className="w-[calc(90%-10rem)] flex gap-4 items-center">
              <Progress
                staticColor={indicatorColor}
                value={conversionPercentage}
                className="h-2"
                indicatorClassname="h-2 border-r borders-border-widget-graph"
                style={{ backgroundColor: barColor }}
              />
              <Text as="span" variant="smBody">
                {`${conversionPercentage.toFixed(1)}%`}
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ConversionDrilldown;
